import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { message } from 'antd';
import { Department as DepartmentType, UserDetail } from '@shared/types';
import { getCurrentMonth, getCurrentYear } from '@/utils/dateHelpers';
import { departmentsService } from '@/services/departments.service';
import { apiService } from '@/services/api.service';
import { usersService } from '@/services/users.service';
import { handleApiError } from '@/utils/errorHandler';
import { exportAttendanceToExcel } from '@/features/timekeeping/exportAttendanceExcel';
import type { DayData, EmployeeTimesheet } from '@/features/timekeeping/types';
import { calculateSummary, isSunday, type EmployeeSummary } from '@/features/timekeeping/summary';

// Helper dùng chung để load toàn bộ user theo phân trang
// Được dùng trong nhiều hook timekeeping để tránh lặp code
const fetchAllUsers = async (isActive?: boolean): Promise<UserDetail[]> => {
  const allUsers: UserDetail[] = [];
  const limit = 200;
  let page = 1;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await usersService.getUsers({ page, limit, isActive });
    if (!res || !res.data) break;

    allUsers.push(...res.data);

    if (res.data.length < limit) break;
    page += 1;
  }

  return allUsers;
};

export interface TimesheetTableRecord {
  key: string;
  employeeName: string;
  employeeCode: string;
  avatar?: string | null;
  summary?: EmployeeSummary;
  // dynamic day fields: day_1, day_2, ...
  [key: string]: any;
}

export interface UseTimesheetTableResult {
  // filters
  searchText: string;
  setSearchText: (value: string) => void;
  selectedDepartment: string | null;
  setSelectedDepartment: (value: string | null) => void;
  selectedMonth: number | null;
  setSelectedMonth: (value: number | null) => void;
  selectedYear: number | null;
  setSelectedYear: (value: number | null) => void;
  hasActiveFilter: boolean;

  // data
  departments: Array<{ id: string; name: string }>;
  allDays: DayData[];
  loading: boolean;
  tableData: TimesheetTableRecord[];
  paginatedData: TimesheetTableRecord[];

  // error state để page có thể hiển thị lỗi
  error: string | null;

  // pagination
  currentPage: number;
  pageSize: number;
  handlePageChange: (page: number, size?: number) => void;
  handlePageSizeChange: (current: number, size: number) => void;

  // selection + summary
  selectedEmployee: string | null;
  setSelectedEmployee: (id: string | null) => void;
  selectedEmployeeSummary: EmployeeSummary | null;
  selectedEmployeeInfo: EmployeeTimesheet | null;

  // actions
  handleClearFilter: () => void;
  handleExport: () => Promise<void>;
}

export const useTimesheetTable = (): UseTimesheetTableResult => {
  const [searchParams] = useSearchParams();

  const getInitialMonth = (): number | null => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      const month = parseInt(monthParam, 10);
      if (month >= 1 && month <= 12) return month;
    }
    return getCurrentMonth();
  };

  const getInitialYear = (): number | null => {
    const yearParam = searchParams.get('year');
    if (yearParam) {
      const year = parseInt(yearParam, 10);
      if (year >= 2000 && year <= 2100) return year;
    }
    return getCurrentYear();
  };

  const [searchText, setSearchText] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>('Tất cả');
  const [selectedMonth, setSelectedMonth] = useState<number | null>(getInitialMonth());
  const [selectedYear, setSelectedYear] = useState<number | null>(getInitialYear());
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [employeesData, setEmployeesData] = useState<EmployeeTimesheet[]>([]);
  const [error, setError] = useState<string | null>(null);

  // sync month/year from URL
  useEffect(() => {
    const monthParam = searchParams.get('month');
    const yearParam = searchParams.get('year');

    if (monthParam) {
      const month = parseInt(monthParam, 10);
      if (month >= 1 && month <= 12) setSelectedMonth(month);
    }
    if (yearParam) {
      const year = parseInt(yearParam, 10);
      if (year >= 2000 && year <= 2100) setSelectedYear(year);
    }
  }, [searchParams]);

  // days in month
  const allDays = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];
    const daysInMonth = dayjs(`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`).daysInMonth();
    const days: DayData[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = dayjs(
        `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
      );
      days.push({ date, dayNumber: day });
    }
    return days;
  }, [selectedMonth, selectedYear]);

  // fetch timesheet data
  const fetchTimesheetData = useCallback(async () => {
    if (!selectedMonth || !selectedYear) {
      setEmployeesData([]);
      return;
    }

    try {
      setLoading(true);

      const [workStatsResponse, usersList, attendanceFirstPage] = await Promise.all([
        apiService.get<{
          success: boolean;
          month: number;
          year: number;
          users: Array<{
            userId: number;
            fullName: string;
            employeeCode: string;
            email: string;
            dailyData: Array<{
              day: number;
              workingHours: number;
              overtimeHours: number;
              violationMinutes: number;
              status: 'WORK' | 'LEAVE' | 'NO_DATA' | 'DAYOFF';
              mealAllowanceEligible?: boolean;
            }>;
            summary: {
              totalWorkingHours: number;
              totalOvertimeHours: number;
              totalViolationMinutes: number;
              totalDaysWorked: number;
              totalLeaveDays: number;
              totalMealAllowanceDays: number;
            };
          }>;
        }>(`/attendance/work-stats?month=${selectedMonth}&year=${selectedYear}`),
        fetchAllUsers(true),
        apiService.get<{
          success: boolean;
          data: Array<{
            id: number;
            userId: number;
            date: string;
            time?: string;
            type?: string;
            note: string | null;
          }>;
          pagination?: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
          };
        }>(`/attendance/all?month=${selectedMonth}&year=${selectedYear}&page=1&limit=100`, true).catch(() => null),
      ]);

      if (!workStatsResponse.success) throw new Error('Không thể tải dữ liệu bảng công');

      // Backend /attendance/all giới hạn limit tối đa 100 → cần phân trang để lấy đủ note.
      const allAttendancesInMonth: Array<{
        id: number;
        userId: number;
        date: string;
        time?: string;
        type?: string;
        note: string | null;
      }> = [];

      if (attendanceFirstPage?.success && Array.isArray(attendanceFirstPage.data)) {
        allAttendancesInMonth.push(...attendanceFirstPage.data);

        const totalPages = Math.max(1, Number(attendanceFirstPage.pagination?.totalPages || 1));
        const limitNum = Math.max(1, Number(attendanceFirstPage.pagination?.limit || 100));

        // Fallback: nếu backend không trả pagination, dùng data.length < limit để stop.
        if (!attendanceFirstPage.pagination && attendanceFirstPage.data.length < limitNum) {
          // done
        } else if (totalPages > 1) {
          for (let page = 2; page <= totalPages; page += 1) {
            try {
              const res = await apiService.get<{
                success: boolean;
                data: Array<{
                  id: number;
                  userId: number;
                  date: string;
                  time?: string;
                  type?: string;
                  note: string | null;
                }>;
              }>(
                `/attendance/all?month=${selectedMonth}&year=${selectedYear}&page=${page}&limit=100`,
                true,
              );

              if (res?.success && Array.isArray(res.data) && res.data.length) {
                allAttendancesInMonth.push(...res.data);
              } else {
                break;
              }

              // safety: stop nếu backend trả ít hơn limit
              if (!res?.data || res.data.length < 100) break;
            } catch {
              break;
            }
          }
        }
      }

      // Group attendance theo userId + date giống timekeeping để lấy:
      // - checkIn sớm nhất
      // - checkOut muộn nhất
      // - note (ưu tiên note có STATUS:)
      // Key format: "userId-YYYY-MM-DD"
      const attendanceDayMap = new Map<
        string,
        {
          checkIn?: string; // HH:mm
          checkOut?: string; // HH:mm
          note?: string;
        }
      >();

      const normalizeHHmm = (timeStr?: string): string | null => {
        if (!timeStr) return null;
        const t = timeStr.toString().slice(0, 5);
        return /^\d{2}:\d{2}$/.test(t) ? t : null;
      };

      const shouldPreferNote = (candidate: string, existing?: string): boolean => {
        if (!candidate) return false;
        if (!existing) return true;
        if (candidate.includes('STATUS:') && !existing.includes('STATUS:')) return true;
        if (candidate.length > existing.length) return true;
        return false;
      };

      if (allAttendancesInMonth.length) {
        allAttendancesInMonth.forEach((att: any) => {
          if (!att?.userId || !att?.date) return;

          const dateStr = dayjs(att.date).format('YYYY-MM-DD');
          const key = `${att.userId}-${dateStr}`;
          const existing = attendanceDayMap.get(key) ?? {};

          const hhmm = normalizeHHmm(att.time);
          const type = (att.type || '').toString().toUpperCase();
          const note = (att.note || undefined) as string | undefined;

          if (type === 'CHECK_IN' && hhmm) {
            if (!existing.checkIn || hhmm < existing.checkIn) {
              existing.checkIn = hhmm;
            }
            if (note && shouldPreferNote(note, existing.note)) {
              existing.note = note;
            }
          }

          if (type === 'CHECK_OUT' && hhmm) {
            if (!existing.checkOut || hhmm > existing.checkOut) {
              existing.checkOut = hhmm;
            }
            if (note && shouldPreferNote(note, existing.note)) {
              existing.note = note;
            }
          }

          // Fallback: nếu record không có type/time nhưng có note thì vẫn lưu để bắt STATUS nghỉ phép
          if (!type && note && shouldPreferNote(note, existing.note)) {
            existing.note = note;
          }

          attendanceDayMap.set(key, existing);
        });
      }

      const userDepartmentMap = new Map<number, number | null>();
      const userAvatarMap = new Map<number, string | null>();
      const nonAdminUsers =
        Array.isArray(usersList) && usersList.length
          ? usersList.filter((u: any) => (u.role || '').toString().toLowerCase() !== 'admin')
          : [];

      if (nonAdminUsers.length) {
        nonAdminUsers.forEach((user: any) => {
          userDepartmentMap.set(user.id, user.departmentId || null);
          userAvatarMap.set(user.id, (user.avatar || null) as string | null);
        });
      }

      const transformedData: EmployeeTimesheet[] = workStatsResponse.users.map((user) => {
        const today = dayjs().startOf('day');

        const days: DayData[] = user.dailyData.map((daily) => {
          const date = dayjs(
            `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${daily.day
              .toString()
              .padStart(2, '0')}`
          ).startOf('day');

          // Lấy dữ liệu theo ngày từ attendance records (note + checkin/checkout)
          const dateKey = `${user.userId}-${date.format('YYYY-MM-DD')}`;
          const dayInfo = attendanceDayMap.get(dateKey);
          const note = dayInfo?.note;

          const dayData: DayData = {
            date,
            dayNumber: daily.day,
            checkIn: dayInfo?.checkIn,
            checkOut: dayInfo?.checkOut,
          };

          // Hàm helper để kiểm tra note có chứa thông tin về nghỉ phép không
          // Logic giống hệt timekeeping (useTimekeepingManagement.ts line 583-601)
          const checkLeaveStatus = (noteText: string | undefined): 'leave' | 'unpaidLeave' | null => {
            if (!noteText) return null;
            
            // Kiểm tra format STATUS: trước (ưu tiên)
            const statusMatch = noteText.match(/STATUS:([^\n]+)/);
            if (statusMatch) {
              const savedStatus = statusMatch[1].trim();
              if (savedStatus === 'Vắng có lương') {
                return 'leave';
              } else if (savedStatus === 'Vắng không lương') {
                return 'unpaidLeave';
              }
            }
            
            // Kiểm tra từ khóa trong note (backward compatibility)
            if (noteText.includes('Vắng có lương')) {
              return 'leave';
            } else if (noteText.includes('Vắng không lương')) {
              return 'unpaidLeave';
            }
            
            return null;
          };

          // 0) Ưu tiên nghỉ phép theo note (giống timekeeping)
          const leaveStatusFromNote = checkLeaveStatus(note);
          if (leaveStatusFromNote) {
            dayData.status = leaveStatusFromNote;
            return dayData;
          }

          // 1) Nếu có check-in mà chưa có check-out:
          // - Ngày trước: luôn cần kiểm tra
          // - Hôm nay: sau 6 tiếng vẫn chưa check-out → cần kiểm tra
          if (dayInfo?.checkIn && !dayInfo?.checkOut && !isSunday(date)) {
            const now = dayjs();
            if (date.isBefore(today, 'day')) {
              dayData.status = 'pending';
              return dayData;
            }
            if (date.isSame(today, 'day')) {
              const checkInDateTime = dayjs(`${date.format('YYYY-MM-DD')} ${dayInfo.checkIn}:00`);
              if (now.diff(checkInDateTime, 'minute') >= 6 * 60) {
                dayData.status = 'pending';
                return dayData;
              }
            }
          }

          if (daily.status === 'LEAVE') {
            // Kiểm tra note để phân biệt "Vắng có lương" và "Vắng không lương"
            // Nếu không có note/STATUS, mặc định là "Vắng có lương"
            dayData.status = 'leave';
          } else if (daily.status === 'NO_DATA' && !isSunday(date)) {
            if (date.isSame(today, 'day')) {
              dayData.status = 'today';
            } else if (date.isBefore(today)) {
              // Không có leave/pending → vắng mặt
              dayData.status = 'absent';
            }
          } else if (daily.status === 'WORK') {
            // Tính hệ số: tối đa 1.000, không bao gồm tăng ca
            const baseCoefficient = daily.workingHours / 8;
            dayData.coefficient = Math.min(Math.round(baseCoefficient * 1000) / 1000, 1.0);
            if (daily.violationMinutes > 0) dayData.lateMinutes = daily.violationMinutes;
            if (daily.overtimeHours > 0) {
              dayData.overtimeHours = Math.round(daily.overtimeHours * 10) / 10;
            }
          }

          return dayData;
        });

        const departmentId = userDepartmentMap.get(user.userId) || null;
        const avatar = userAvatarMap.get(user.userId) ?? null;

        return {
          id: user.userId.toString(),
          employeeName: user.fullName,
          employeeCode: user.employeeCode,
          avatar,
          days,
          departmentId,
          backendSummary: user.summary,
        } as EmployeeTimesheet & {
          departmentId?: number | null;
          backendSummary?: {
            totalWorkingHours: number;
            totalOvertimeHours: number;
            totalViolationMinutes: number;
            totalDaysWorked: number;
            totalLeaveDays: number;
            totalMealAllowanceDays: number;
          };
        };
      });

      const adminIds = new Set<number>();
      if (Array.isArray(usersList) && usersList.length) {
        usersList.forEach((u: any) => {
          if ((u.role || '').toString().toLowerCase() === 'admin') {
            adminIds.add(Number(u.id));
          }
        });
      }

      const filteredTransformed = transformedData.filter((t) => !adminIds.has(Number(t.id)));

      const existingIds = new Set(filteredTransformed.map((t) => Number(t.id)));
      if (nonAdminUsers && nonAdminUsers.length) {
        const today = dayjs().startOf('day');
        nonAdminUsers.forEach((u: any) => {
          if (!existingIds.has(Number(u.id))) {
            const emptyDays: DayData[] = allDays.map((d) => {
              const dayData: DayData = { date: d.date, dayNumber: d.dayNumber };
              if (!isSunday(d.date)) {
                if (d.date.isSame(today, 'day')) {
                  dayData.status = 'today';
                } else if (d.date.isBefore(today)) {
                  dayData.status = 'absent';
                }
              }
              return dayData;
            });
            const deptId = userDepartmentMap.get(u.id) || null;
            const avatar = (u.avatar || null) as string | null;
            filteredTransformed.push({
              id: u.id.toString(),
              employeeName: (u.fullName || u.name || '').toString(),
              employeeCode: (u.employeeCode || '').toString(),
              days: emptyDays,
              departmentId: deptId,
              avatar,
            } as EmployeeTimesheet & { departmentId?: number | null });
          }
        });
      }

      setEmployeesData(filteredTransformed);
      // Clear error khi load dữ liệu thành công
      setError(null);
    } catch (error: any) {
      console.error('Error fetching timesheet data:', error);
      const errorMessage = handleApiError(error, 'Không thể tải dữ liệu bảng công');
      // Ưu tiên message từ handleApiError nếu có
      setError(typeof errorMessage === 'string' ? errorMessage : 'Không thể tải dữ liệu bảng công');
      setEmployeesData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, allDays]);

  useEffect(() => {
    fetchTimesheetData();
  }, [fetchTimesheetData]);

  // departments
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await departmentsService.getDepartments({ limit: 100 });
      const depts = response.data
        .filter((dept: DepartmentType) => dept.isActive)
        .map((dept: DepartmentType) => ({
          id: dept.id.toString(),
          name: dept.name,
        }));
      setDepartments(depts);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // filtered employees
  const filteredEmployees = useMemo(() => {
    let filtered = employeesData;

    if (selectedDepartment && selectedDepartment !== 'Tất cả') {
      const dept = departments.find((d) => d.name === selectedDepartment);
      if (dept) {
        filtered = filtered.filter((emp) => {
          const empDeptId = (emp as any).departmentId;
          return empDeptId?.toString() === dept.id;
        });
      }
    }

    if (searchText.trim()) {
      const term = searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (emp) =>
          emp.employeeName.toLowerCase().includes(term) || emp.employeeCode.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [employeesData, searchText, selectedDepartment, departments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedDepartment, selectedMonth, selectedYear]);

  // table records
  const tableData: TimesheetTableRecord[] = useMemo(() => {
    return filteredEmployees
      .filter((emp) => emp && (emp.employeeName?.toString().trim() || emp.employeeCode?.toString().trim()))
      .map((emp) => {
        const record: TimesheetTableRecord = {
          key: emp.id,
          employeeName: emp.employeeName,
          employeeCode: emp.employeeCode,
          avatar: emp.avatar ?? null,
          summary: calculateSummary(emp),
        };

        allDays.forEach((day) => {
          const empDay = emp.days.find((d) => d.date.isSame(day.date, 'day'));
          record[`day_${day.dayNumber}`] =
            empDay ??
            ({
              date: day.date,
              dayNumber: day.dayNumber,
            } as DayData);
        });

        return record;
      });
  }, [filteredEmployees, allDays]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, currentPage, pageSize]);

  const selectedEmployeeSummary = useMemo(() => {
    if (!selectedEmployee) return null;
    const employee = filteredEmployees.find((emp) => emp.id === selectedEmployee);
    return employee ? calculateSummary(employee) : null;
  }, [selectedEmployee, filteredEmployees]);

  const selectedEmployeeInfo = useMemo(() => {
    if (!selectedEmployee) return null;
    return filteredEmployees.find((emp) => emp.id === selectedEmployee) ?? null;
  }, [selectedEmployee, filteredEmployees]);

  const handleClearFilter = useCallback(() => {
    setSearchText('');
    setSelectedDepartment('Tất cả');
    setSelectedMonth(getCurrentMonth());
    setSelectedYear(getCurrentYear());
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (page: number, size?: number) => {
      setCurrentPage(page);
      if (size && size !== pageSize) {
        setPageSize(size);
      }
    },
    [pageSize]
  );

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(async () => {
    if (!selectedMonth || !selectedYear || filteredEmployees.length === 0) {
      message.warning('Không có dữ liệu để xuất file');
      return;
    }

    try {
      const exportEmployees = selectedEmployee
        ? filteredEmployees.filter((e) => e.id === selectedEmployee)
        : filteredEmployees;

      let departmentName: string | null = null;
      let employeeName: string | null = null;
      let employeeCode: string | null = null;

      if (selectedEmployee && exportEmployees.length > 0) {
        const emp = exportEmployees[0];
        employeeName = emp.employeeName;
        employeeCode = emp.employeeCode;
      } else if (exportEmployees.length === 1 && searchText.trim()) {
        const emp = exportEmployees[0];
        employeeName = emp.employeeName;
        employeeCode = emp.employeeCode;
      } else if (selectedDepartment && selectedDepartment !== 'Tất cả') {
        departmentName = selectedDepartment;
      }

      await exportAttendanceToExcel(exportEmployees, allDays, selectedMonth, selectedYear, {
        departmentName,
        employeeName,
        employeeCode,
      });

      message.success('Xuất file Excel thành công');
    } catch (error: any) {
      console.error('Error exporting file:', error);
      message.error('Không thể xuất file: ' + (error.message || 'Lỗi không xác định'));
    }
  }, [filteredEmployees, selectedMonth, selectedYear, allDays, selectedEmployee, selectedDepartment, searchText]);

  const hasActiveFilter = Boolean(
    searchText.trim() ||
      (selectedDepartment && selectedDepartment !== 'Tất cả') ||
      selectedMonth !== getCurrentMonth() ||
      selectedYear !== getCurrentYear()
  );

  return {
    searchText,
    setSearchText,
    selectedDepartment,
    setSelectedDepartment,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    hasActiveFilter,
    departments,
    allDays,
    loading,
    tableData,
    paginatedData,
    // expose error state cho UI
    error,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    selectedEmployee,
    setSelectedEmployee,
    selectedEmployeeSummary,
    selectedEmployeeInfo,
    handleClearFilter,
    handleExport,
  };
};
